import React from 'react';
import { EmailBlock, TextBlockContent } from '../../types';
import { MERGE_TAGS } from '../../utils/mergeTagHelper';
import { MentionAutocomplete } from '../MentionAutocomplete';

interface TextPropertiesProps {
  block: EmailBlock;
  onUpdate: (updates: Partial<TextBlockContent>) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
  isDarkMode: boolean;
}

export const TextProperties: React.FC<TextPropertiesProps> = ({
  block,
  onUpdate,
  mergeTags = MERGE_TAGS,
  isDarkMode
}) => {
  const content = block.content as TextBlockContent;

  return (
    <div className="space-y-4">
      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Text Content
        </label>
        <MentionAutocomplete
          value={content.text || ''}
          onChange={(value) => onUpdate({ text: value })}
          placeholder="Enter text content... Type @ to insert placeholders"
          isDarkMode={isDarkMode}
          rows={4}
          multiline={true}
        />
        <div className="mt-2">
          <p className={`
            text-xs
            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
          `}>
            💡 Type <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700">@</code> to insert placeholders
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Font Size
          </label>
          <input
            type="number"
            value={content.fontSize || 16}
            onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 16 })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>

        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Font Weight
          </label>
          <select
            value={content.fontWeight || 'normal'}
            onChange={(e) => onUpdate({ fontWeight: e.target.value as any })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
            <option value="lighter">Lighter</option>
            <option value="bolder">Bolder</option>
          </select>
        </div>
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Text Color
        </label>
        <input
          type="color"
          value={content.color || '#000000'}
          onChange={(e) => onUpdate({ color: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Background Color
        </label>
        <input
          type="color"
          value={content.backgroundColor || '#ffffff'}
          onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Text Align
        </label>
        <select
          value={content.textAlign || 'left'}
          onChange={(e) => onUpdate({ textAlign: e.target.value as any })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="justify">Justify</option>
        </select>
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Padding
        </label>
        <input
          type="text"
          value={content.padding || '10px'}
          onChange={(e) => onUpdate({ padding: e.target.value })}
          placeholder="10px"
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        />
      </div>
    </div>
  );
};
